import { Dispatch } from 'redux'
import { getApiErrorMessage, getCSRFToken, hasApiErrorCode } from '../../lib/utils'
import { Invitation } from '../../types/Invitation'
import {
	SENT_INVITATIONS_ERROR,
	SENT_INVITATIONS_FAILURE_UPDATE,
	SENT_INVITATIONS_FETCHING,
	SENT_INVITATIONS_INSERT,
	SENT_INVITATIONS_SUCCESS,
	SENT_INVITATIONS_UPDATED_RESEND,
	SENT_INVITATIONS_UPDATED_REVOKED,
	SENT_INVITATIONS_UPDATING,
} from '../slices/companySentInvitationsSlice'
import {
	SUPPLYCHAIN_INVITATIONS_CREATE_ERROR,
	SUPPLYCHAIN_INVITATIONS_CREATE_SENDING,
	SUPPLYCHAIN_INVITATIONS_CREATE_SENT,
	SUPPLYCHAIN_INVITATION_RESETEMAIL,
	SUPPLYCHAIN_INVITATION_SETEMAIL,
} from '../slices/createSupplyChainInvitation'
import { NeoniApi, RequestOptions } from '../../lib/api'

export const getSentInvitations = (api: NeoniApi) => async (dispatch: Dispatch) => {
	dispatch({
		type: SENT_INVITATIONS_FETCHING,
	})

	try {
		const body = await api.get(`/supplyChainInvitations/sent`)
		body.invitations.sort((a: Invitation, b: Invitation) => (a.createdAt > b.createdAt ? -1 : 1))

		dispatch({
			type: SENT_INVITATIONS_SUCCESS,
			payload: body.invitations,
		})
	} catch (error) {
		dispatch({
			type: SENT_INVITATIONS_ERROR,
			payload: getApiErrorMessage(error, 'Could not load invitations. Please try again.'),
		})
	}
}

export const revokeInvitationCompany =
	(api: NeoniApi, invitationId: string, status: string) => async (dispatch: Dispatch) => {
		// Mark invitation as update in progress
		dispatch({
			type: SENT_INVITATIONS_UPDATING,
			payload: invitationId,
		})

		try {
			const csrf = await getCSRFToken()
			const body = await api.put(`supplyChainInvitations/${invitationId}`, {
				status, _csrf: csrf
			});

			if (body.success === true) {
				// Successfully revoked
				dispatch({
					type: SENT_INVITATIONS_UPDATED_REVOKED,
					payload: body.invitation,
				})
			} else {
				// Error
				dispatch({
					type: SENT_INVITATIONS_FAILURE_UPDATE,
					payload: { id: invitationId, error: getApiErrorMessage({ body }, 'Could not update the invitation. Please try again.') },
				})
			}
		} catch (error) {
			dispatch({
				type: SENT_INVITATIONS_FAILURE_UPDATE,
				payload: { id: invitationId, error: getApiErrorMessage(error, 'Could not update the invitation. Please try again.') },
			})
		}
	}

type ResendResult =
	| { status: 'sent' }
	| { status: 'failed'; message: string; retrySafe: boolean }
	| { status: 'cancelled' }

export const resendInvitationCompany = (api: NeoniApi, invitationId: string, options?: RequestOptions) => async (dispatch: Dispatch): Promise<ResendResult> => {
	// Mark invitation as update in progress
	dispatch({
		type: SENT_INVITATIONS_UPDATING,
		payload: invitationId,
	})

	try {
		const csrf = await getCSRFToken()
		if (options?.signal?.aborted) return { status: 'cancelled' }
		const payload = {
			inviteId: invitationId, _csrf: csrf
		}
		const body = options
			? await api.post('supplyChainInvitations/resend', payload, options)
			: await api.post('supplyChainInvitations/resend', payload)
		if (options?.signal?.aborted) return { status: 'cancelled' }

		if (body.success === true) {
			dispatch({
				type: SENT_INVITATIONS_UPDATED_RESEND,
				payload: { revokedInvite: body.revokedInvite, invite: body.invitation },
			})
			return { status: 'sent' }
		}
		const message = getApiErrorMessage({ body }, 'Could not resend the invitation. Please try again.')
		dispatch({ type: SENT_INVITATIONS_FAILURE_UPDATE, payload: { id: invitationId, error: message } })
		return { status: 'failed', message, retrySafe: true }
	} catch (error) {
		if (options?.signal?.aborted) return { status: 'cancelled' }
		const retrySafe = hasApiErrorCode(error, 'READ_ONLY_ACCESS') || hasApiErrorCode(error, 'ACCESS_AUDIT_UNAVAILABLE')
		const message = getApiErrorMessage(error, 'Could not update the invitation. Please try again.')
		dispatch({
			type: SENT_INVITATIONS_FAILURE_UPDATE,
			payload: { id: invitationId, error: message },
		})
		return { status: 'failed', message, retrySafe }
	}
}

export const createInvitationCompany =
	(api: NeoniApi, companyId: string, email: string, sendEmail: boolean, supplierId?: string) =>
	async (dispatch: Dispatch) => {
		try {
			dispatch({
				type: SUPPLYCHAIN_INVITATIONS_CREATE_SENDING,
			})
			dispatch({
				type: SUPPLYCHAIN_INVITATION_SETEMAIL,
				payload: { companyId, email, supplierId, sendEmail },
			})

			const csrf = await getCSRFToken()

			const body = await api.post(`supplyChainInvitations`, {
				companyId, email, supplierId, _csrf: csrf, sendEmail
			});

			if (body.success === true) {
				// Successfully invited
				dispatch({
					type: SUPPLYCHAIN_INVITATIONS_CREATE_SENT,
					payload: body.invitation,
				})
				dispatch({
					type: SENT_INVITATIONS_INSERT,
					payload: body.invitation,
				})
				dispatch({
					type: SUPPLYCHAIN_INVITATION_RESETEMAIL,
				})
			} else {
				// Error
				dispatch({
					type: SUPPLYCHAIN_INVITATIONS_CREATE_ERROR,
					payload: getApiErrorMessage({ body }, 'Could not send the invitation. Please try again.'),
				})
			}
		} catch (error) {
			dispatch({
				type: SUPPLYCHAIN_INVITATIONS_CREATE_ERROR,
				payload: getApiErrorMessage(error, 'Could not send the invitation. Please try again.'),
			})
		}
	}
